// platform/src/components/mw/MwReveal.tsx
//
// Wrapper que anima children con fade-up cuando entran en viewport.
// Usa IntersectionObserver vanilla: sin dependencias.
//
// Comportamiento:
//   - Antes de entrar en viewport: opacity 0, translateY 24px.
//   - Al entrar: anima a opacity 1, translateY 0 con easing Apple.
//   - Solo se dispara una vez.
//
// Accesibilidad: si prefers-reduced-motion está activo, se renderiza
// sin animación (revealed inmediato).
//
// Implementación: lazy init de estado evita setState síncrono en
// effect body (lint react-hooks/set-state-in-effect).

import { useEffect, useRef, useState, type ReactNode } from 'react'

interface MwRevealProps {
  children: ReactNode
  delay?: number
  distance?: number
  className?: string
  as?: 'div' | 'section' | 'article' | 'header' | 'footer'
}

function detectReducedMotion(): boolean {
  if (typeof window === 'undefined') return false
  try {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches
  } catch {
    return false
  }
}

export function MwReveal({
  children,
  delay = 0,
  distance = 24,
  className = '',
  as: Component = 'div',
}: MwRevealProps) {
  const ref = useRef<HTMLDivElement>(null)
  // Init lazy: si reducedMotion ya es true, revealed=true desde el
  // primer render. Si no, false hasta que el observer dispare.
  const [reducedMotion] = useState(detectReducedMotion)
  const [revealed, setRevealed] = useState(() => reducedMotion)

  useEffect(() => {
    if (revealed) return // ya está, no hacemos nada
    if (typeof window === 'undefined') return
    const node = ref.current
    if (!node) return

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setRevealed(true)
            observer.disconnect()
          }
        }
      },
      { threshold: 0.1, rootMargin: '-10% 0px' },
    )
    observer.observe(node)
    return () => observer.disconnect()
  }, [revealed])

  const style: React.CSSProperties = {
    opacity: revealed ? 1 : 0,
    transform: revealed ? 'translateY(0)' : `translateY(${distance}px)`,
    transition: `opacity 700ms var(--mw-ease) ${delay}ms, transform 700ms var(--mw-ease) ${delay}ms`,
    willChange: revealed ? 'auto' : 'opacity, transform',
  }

  return (
    <Component ref={ref as React.Ref<HTMLDivElement>} className={className} style={style}>
      {children}
    </Component>
  )
}
